/**
 * Investors React Query Hook
 *
 * Hook for fetching investor entity data using React Query
 */

import { useMutation } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { portalQueryKeys } from '@/portal/shared/hooks/queryKeys';
import { InvestorService, InvestorEntity } from '../services/investor-service';

/**
 * Hook to fetch all investor entities accessible by the current contact
 *
 * Returns a list of investor entities (legal entities through which
 * the contact makes investments). Currently returns a single investor,
 * but structured to support multi-entity access in the future.
 */
export const useInvestors = () => {
  return useApiQuery<InvestorEntity[]>(
    portalQueryKeys.investors.list(),
    () => InvestorService.getInvestors(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - investor data changes infrequently
      // Always refetch on mount so changes applied by an approved change request
      // (in the separate admin app) show up when the profile page is reopened,
      // without requiring a manual browser refresh.
      refetchOnMount: 'always',
    }
  );
};

/**
 * Hook to validate an IBAN and resolve the matching SWIFT/BIC and bank name.
 *
 * Used by the banking change-request form to power the bank-name autocomplete.
 */
export const useValidateIban = () => {
  return useMutation({
    mutationFn: (iban: string) => InvestorService.validateIban(iban),
  });
};

/**
 * Hook to validate a SWIFT/BIC and resolve its country and bank name.
 *
 * Used by the banking change-request form for non-IBAN (e.g. US) accounts.
 */
export const useValidateSwift = () => {
  return useMutation({
    mutationFn: (swift: string) => InvestorService.validateSwift(swift),
  });
};
